// App Orchestrator Types
// ✅ NO MAGIC STRINGS: Use typed enums and constants

export enum OrchestratorStatus {
  INITIALIZING = "initializing",
  READY = "ready",
  RUNNING = "running",
  PAUSED = "paused",
  ERROR = "error",
  SHUTDOWN = "shutdown",
}

export enum ModuleLifecycleEvent {
  MODULE_INITIALIZING = "module_initializing",
  MODULE_READY = "module_ready",
  MODULE_ERROR = "module_error",
  MODULE_SHUTDOWN = "module_shutdown",
}

export enum OrchestratorEvent {
  ORCHESTRATOR_START = "orchestrator_start",
  ORCHESTRATOR_READY = "orchestrator_ready",
  ORCHESTRATOR_ERROR = "orchestrator_error",
  ORCHESTRATOR_SHUTDOWN = "orchestrator_shutdown",
  MODULE_REGISTERED = "module_registered",
  MODULE_UNREGISTERED = "module_unregistered",
}

// Business domain modules — map 1:1 to src/domains/<name>/module/
export const DOMAIN_MODULES = {
  AUTH: "auth",
  ORGANIZATION: "organization",
  PROJECT: "project",
  APPLICATION: "application",
  USER: "user",
} as const;

// Platform service modules — internal to src/platform/
export const PLATFORM_MODULES = {
  NAVIGATION: "navigation",
  DATA: "data",
  UI: "ui",
  NOTIFICATIONS: "notifications",
} as const;

// Combined for backward compatibility
export const MODULE_NAMES = {
  ...DOMAIN_MODULES,
  ...PLATFORM_MODULES,
} as const;

export type ModuleName = (typeof MODULE_NAMES)[keyof typeof MODULE_NAMES];

export interface ModuleInfo {
  name: ModuleName;
  status: OrchestratorStatus;
  dependencies: ModuleName[];
  events: string[];
  initialized: boolean;
  error?: string;
}

export interface OrchestratorConfig {
  autoStart: boolean;
  enableLogging: boolean;
  enableMetrics: boolean;
  shutdownTimeout: number;
  moduleTimeout: number;
}

export interface OrchestratorState {
  status: OrchestratorStatus;
  modules: Map<ModuleName, ModuleInfo>;
  startTime?: Date;
  error?: string;
}

export interface ModuleRegistration {
  name: ModuleName;
  dependencies?: ModuleName[];
  events?: string[];
  onInitialize?: () => Promise<void>;
  onShutdown?: () => Promise<void>;
  onError?: (error: Error) => void;
}

export interface OrchestratorMetrics {
  totalModules: number;
  activeModules: number;
  errorModules: number;
  uptime: number;
  eventCount: number;
  lastEventTime?: Date;
}

export const ORCHESTRATOR_ERRORS = {
  MODULE_ALREADY_REGISTERED: "Module is already registered",
  MODULE_NOT_FOUND: "Module not found",
  CIRCULAR_DEPENDENCY: "Circular dependency detected",
  DEPENDENCY_NOT_FOUND: "Dependency not found",
  INITIALIZATION_FAILED: "Module initialization failed",
  SHUTDOWN_FAILED: "Module shutdown failed",
  INVALID_MODULE_NAME: "Invalid module name",
  TIMEOUT_ERROR: "Operation timed out",
} as const;

export type OrchestratorErrorType = keyof typeof ORCHESTRATOR_ERRORS;

export class OrchestratorError extends Error {
  constructor(
    public readonly type: OrchestratorErrorType,
    public readonly moduleName?: ModuleName,
    public readonly details?: string,
  ) {
    super(ORCHESTRATOR_ERRORS[type]);
    this.name = "OrchestratorError";
  }
}
